import { ReactNode } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { InlineNotification, InlineNotificationProps, Text, Truncated } from '@frontend/design-system';

type Props = {
  iconName: IconName;
  title: ReactNode | string;
  subtitle?: ReactNode | string;
  secondaryText?: ReactNode | string;
  endAdornment?: ReactNode | string;
  containerStyles?: Interpolation<Theme>;
  hideBoxShadow?: boolean;
  notification?: {
    status: InlineNotificationProps['status'];
    text: ReactNode | string;
  };
  isMuted?: boolean;
};

export const EventBox = ({
  iconName,
  title,
  subtitle,
  secondaryText,
  endAdornment,
  containerStyles,
  hideBoxShadow,
  notification,
  isMuted = false,
}: Props) => {
  const gridTemplateColumns = secondaryText
    ? 'max-content 1fr max-content max-content max-content'
    : 'max-content 1fr max-content max-content';

  return (
    <div className='event-box-item' css={[styles(hideBoxShadow), containerStyles, css({ gridTemplateColumns })]}>
      <Icon name={iconName} css={{ color: isMuted ? 'var(--fabric-color-text-disabled)' : theme.colors.neutral70 }} />

      <div className='col-2'>
        <Truncated
          css={{ color: isMuted ? 'var(--fabric-color-text-disabled)' : 'initial' }}
          textAlign='left'
          as={Text}
          weight='bold'
          size='medium'
        >
          {title}
        </Truncated>
        {subtitle && (
          <Truncated
            css={{ color: isMuted ? 'var(--fabric-color-text-disabled)' : 'initial' }}
            textAlign='left'
            as={Text}
            size='medium'
          >
            {subtitle}
          </Truncated>
        )}

        {notification && (
          <InlineNotification size='small' status={notification.status}>
            <Text size='small'>{notification.text}</Text>
          </InlineNotification>
        )}
      </div>

      {secondaryText && (
        <Text size='medium' color='light'>
          {secondaryText}
        </Text>
      )}

      <Text as='div' size='medium'>
        {endAdornment}
      </Text>
    </div>
  );
};

const styles = (hideBoxShadow?: boolean) => css`
  padding: ${theme.spacing(2)};
  padding-right: 0;
  box-shadow: ${hideBoxShadow ? 'none' : theme.shadows.light};
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.medium};
  display: grid;
  gap: ${theme.spacing(2)};
  width: 100%;
  align-items: center;
  position: relative;

  .col-2 {
    display: grid;
    grid-template-rows: auto auto;
    justify-content: start;
  }
`;
